import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import Lottie from "react-lottie";
import PropTypes from "prop-types";
import { AppMainContext } from "../App";
import { ScriptsContext } from "../App";
import { AccordionContext } from "./AccordionContainer";
import { launchScriptsFromQueue } from "../apiHandlers/scriptLaunchers/launchScriptsFromQueue";
import { accordionConfigData as defaultAccordionState } from "../constants";
import successful_animation from "../assets/successful_animation.json";

export const defaultOptions = {
  loop: false,
  autoplay: false,
  animationData: successful_animation,
  rendererSettings: {
    preserveAspectRatio: "xMidYMid slice",
  },
};
export const RowContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  flex: 1;
`;

export const LottieAnimationContainer = styled.div`
  display: flex;
  height: 100px;
  width: 100px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  padding: 0px;
  margin-top: -12px;
`;

export const ChecksPassedParagraph = styled.p`
  font-family: "Inter";
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  display: flex;
  margin-bottom: 8px;
  align-items: center;
  text-align: center;
  letter-spacing: 0.1px;
  color: #3b4c66;
`;

export const FileNameParagraphNonFileName = styled.p`
  font-family: "Inter";
  font-style: normal;
  font-weight: 400;
  font-size: 13px;
  color: #3b4c66;
`;
export const FileNameParagraphFileName = styled.span`
  font-weight: bold;
  text-decoration: underline;
  color: #6285ff;
`;

export const LaunchScriptButton = styled.button`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 6px 20px;
  background: #6285ff;
  color: white;
  border: 0.5px solid #6285ff;
  border-radius: 3px;
  font-family: "Inter";
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  margin-right: 10px;
  transition: background-color 0.3s ease;
  outline: none; // to remove default browser focus styling

  &:active,
  &:focus {
    background-color: #3e61cc;
  }

  &:hover {
    background-color: #4c74ff;
    border: 0.5px solid #4c74ff;
  }

  &[disabled] {
    background: #ccc;
    color: #888;
    border: 0.5px solid #ccc;
    cursor: not-allowed;

    &:hover,
    &:active,
    &:focus {
      background-color: #ccc;
      border: 0.5px solid #ccc;
    }
  }
`;

export const DeleteFileButton = styled(LaunchScriptButton)`
  background: white;
  color: #6285ff;
  border: 1px solid #6285ff;
  margin-right: 0px;

  &:active,
  &:focus {
    background-color: #4c74ff;
    color: white;
  }

  &:hover {
    background-color: #6285ff;
    color: white;
  }

  &[disabled] {
    background: #ccc;
    color: #888;
    border: 1px solid #ccc;
    cursor: not-allowed;

    &:hover,
    &:active,
    &:focus {
      background-color: #ccc;
      border: 1px solid #ccc;
    }
  }
`;

const styleSheet = {
  container: {
    display: "flex",
    flexDirection: "column",
    height: "auto",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    padding: "5px",
    backgroundColor: "",
  },
  checksPassedMessageRowContainer: {
    marginTop: "-20px",
  },
};

const SuccessContainer = ({ file, id, queues, isSkipped }) => {
  const { appContext } = useContext(AppMainContext);
  const { scriptsContext, setScriptsContext } = useContext(ScriptsContext);
  const { accordionData, setAccordionData } = useContext(AccordionContext);
  const [isPlaying, setIsPlaying] = useState(false);
  // Initialize isButtonClickable to true
  const [isButtonClickable, setIsButtonClickable] = useState(true);
  const defaultFileState = defaultAccordionState[id - 1];
  useEffect(() => {
    setIsPlaying(true);
  }, []);
  const scriptsQueue = queues.scriptsQueue;
  const launchableScriptArr = scriptsQueue.filter(
    (obj) => obj.stepSucceededIdRequiredToLaunch === id
  );
  const launchableScript =
    launchableScriptArr.length > 0 ? launchableScriptArr[0] : null;

  //reset file to null (delete file and enable to return to file upload state)
  const deleteFile = () => {
    const updatedAccordionData = [...accordionData];
    updatedAccordionData[id - 1] = { ...defaultFileState };
    setAccordionData(updatedAccordionData);
  };

  const handleClick = () => {
    setIsButtonClickable(false);
    setScriptsContext({ ...scriptsContext, launchedFromAccordionId: id });
    launchScriptsFromQueue(queues, appContext, setScriptsContext);
  };

  return (
    <div style={styleSheet.container}>
      {!isSkipped && (
        <RowContainer>
          <LottieAnimationContainer>
            <Lottie options={defaultOptions} isPaused={!isPlaying} />
          </LottieAnimationContainer>
        </RowContainer>
      )}
      {!isSkipped && (
        <RowContainer style={styleSheet.checksPassedMessageRowContainer}>
          <ChecksPassedParagraph>{"All Checks Passed!"}</ChecksPassedParagraph>
        </RowContainer>
      )}
      <RowContainer>
        {isSkipped ? (
          <ChecksPassedParagraph>Your file was skipped.</ChecksPassedParagraph>
        ) : (
          <FileNameParagraphNonFileName>
            Your file{" "}
            <FileNameParagraphFileName>{file.name}</FileNameParagraphFileName>
            was successfully validated.
          </FileNameParagraphNonFileName>
        )}
      </RowContainer>
      <RowContainer>
        <LaunchScriptButton
          style={{ display: launchableScript ? "block" : "none" }}
          onClick={() => isButtonClickable && handleClick()}
          disabled={!isButtonClickable}
        >
          Launch Script
        </LaunchScriptButton>
        <DeleteFileButton
          onClick={() => isButtonClickable && deleteFile()}
          disabled={!isButtonClickable}
        >
          {isSkipped ? "Cancel" : "Delete File"}
        </DeleteFileButton>
      </RowContainer>
    </div>
  );
};

SuccessContainer.propTypes = {
  id: PropTypes.number,
  isSkipped: PropTypes.bool,
  file: PropTypes.oneOfType([
    PropTypes.shape({
      lastModified: PropTypes.number,
      name: PropTypes.string,
      size: PropTypes.number,
      type: PropTypes.string,
    }),
    PropTypes.instanceOf(null),
  ]),
  queues: PropTypes.shape({
    scriptsQueue: PropTypes.arrayOf(PropTypes.object),
    filesQueue: PropTypes.arrayOf(PropTypes.object),
  }),
};

export default SuccessContainer;
